import { useAntdTable, useDebounce, useRequest } from 'ahooks';
import { useCompany } from '../../hooks/company';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Popconfirm,
  Popover,
  Row,
  Select,
  SelectProps,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  BaseDepartmentDTO,
  CustomerEventListItem,
  CustomerEventTypeEnum,
  CustomerLoanDTO,
  CustomerPoolEnum,
  ListCustomerEventsParamsDTO,
  ListCustomerLoanParamsDTO,
  Long,
  customerController,
  loanController,
} from '../../api';
import { ColumnsType } from 'antd/es/table';
import CustomerMobile from '../../components/CustomerMobile';
import dayjs from 'dayjs';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import DepartmentSelect from '../../components/DepartmentSelect';
import CustomerChannelSelect from '../../components/CustomerChannelSelect';
import { ActionType, ProColumns, ProTable } from '@ant-design/pro-components';
import '../../styles/common.scss';
import { formatEndTime, formatStartTime } from '../../utils';
import { useLocation, useOutletContext } from 'react-router-dom';
import UserSelect from '../../components/UserSelect';
import { AvatarImg } from '../../components/OSSImage';
import { UserModal } from '../../components/UserModel';
import { customerEventMapping, customerPoolMapping } from '../../constants';
import CustomerDetail from '../../components/CustomerDetail';
import { LastEventDrawer } from '../../components/LastEvent';

export type LoanListSessionQuery = Partial<ListCustomerLoanParamsDTO>;

export function setLoainListSessionQuery(v: LoanListSessionQuery) {
  (window as any).__$$loanListSessionQuery = v;
}

const TypeSelect = ({ ...props }: SelectProps) => {
  return (
    <Select
      {...props}
      options={Object.entries(customerEventMapping)
        .filter(([key]) =>
          [
            CustomerEventTypeEnum.COMMENT,
            CustomerEventTypeEnum.REMARK,
            CustomerEventTypeEnum.VISIT,
            CustomerEventTypeEnum.LOAN,
          ].includes(key as CustomerEventTypeEnum),
        )
        .map(([key, value]) => ({
          label: value,
          value: key,
        }))}
    />
  );
};

const recfreshContext = createContext<{
  refresh: () => void;
  setUser?: (u: { id: I.ID; name: string }) => void;
  setCustomer?: (c: {
    id: I.ID;
    name: string;
    pool?: CustomerPoolEnum;
  }) => void;
  setCustomerEventCustomer?: (c: { id: I.ID; name: string }) => void;
}>({
  refresh: () => {},
});

const flatDepartment = (
  departments: BaseDepartmentDTO[],
  depId?: Long,
): BaseDepartmentDTO[] => {
  if (!depId) {
    return [];
  }
  const dep = departments.find((item) => item.id === depId);
  if (!dep) {
    return [];
  }

  return [dep, ...flatDepartment(departments, dep.parentId)];
};

const UserInfo = ({
  userName,
  userId,
  departmentId,
}: CustomerEventListItem) => {
  const { departments } = useCompany();

  const userDepartments = flatDepartment(departments, departmentId).reverse();

  return (
    <Space direction="vertical">
      <span>
        {userName}（{userId}）
      </span>
      <span>{userDepartments.map((item) => item.name).join('/')}</span>
    </Space>
  );
};

const columns: ProColumns<CustomerEventListItem>[] = [
  {
    dataIndex: 'id',
    hideInSearch: true,
    hideInForm: true,
    title: '跟进 ID',
  },
  {
    title: '区域',
    hideInTable: true,
    dataIndex: 'departmentId',
    search: {
      transform: (departmentId) => ({ eventDepartmentId: [departmentId] }),
    },
    order: 1000,
    renderFormItem: (_, {}) => {
      return <DepartmentSelect />;
    },
  },
  {
    hideInTable: false,
    title: '业务员',
    order: 800,
    hideInForm: true,
    hideInSearch: true,
    renderFormItem: (_, {}) => {
      return <UserSelect allowClear mode="multiple" />;
    },
    render: (_, { userId, userName }) => {
      const { setUser } = useContext(recfreshContext);
      return (
        <>
          {userId && userName && (
            <Button
              type="link"
              onClick={() => setUser?.({ id: userId, name: userName })}
            >
              <Space className="avatar-box">
                <AvatarImg userId={userId} />
                <span>{userName}</span>
              </Space>
            </Button>
          )}
        </>
      );
    },
    search: {
      transform: (userId) => ({ userId }),
    },
  },
  {
    hideInTable: false,
    title: '跟进人',
    order: 800,
    renderFormItem: (_, {}) => {
      return <UserSelect allowClear mode="multiple" />;
    },
    render: (_, { eventUserId: userId, eventUserName: userName }) => {
      const { setUser } = useContext(recfreshContext);
      return (
        <>
          {userId && userName && (
            <Button
              type="link"
              onClick={() => setUser?.({ id: userId, name: userName })}
            >
              <Space className="avatar-box">
                <AvatarImg userId={userId} />
                <span>{userName}</span>
              </Space>
            </Button>
          )}
        </>
      );
    },
    search: {
      transform: (eventUserId) => ({ eventUserId }),
    },
  },
  {
    title: '客户',
    renderFormItem: (_, {}) => {
      return <Input placeholder="客户电话" />;
    },
    search: {
      transform: (key) => ({ key }),
    },
    render: (_, record) => {
      const { setCustomer } = useContext(recfreshContext);
      return (
        <Space direction="vertical">
          <span>
            <Button
              type="link"
              onClick={() => {
                setCustomer?.({
                  id: record.customerId,
                  name: record.customerName,
                  pool: record.pool,
                });
              }}
            >
              {record.customerName}
            </Button>
            <Tag>{customerPoolMapping[record.pool]}</Tag>
          </span>
          <CustomerMobile mobile={record.mobile} />
        </Space>
      );
    },
  },
  {
    title: '类型',
    dataIndex: 'type',
    renderFormItem: (_, {}) => {
      return <TypeSelect />;
    },
    search: {
      transform: (type) => ({ type: [type] }),
    },
    render: (_, record) => {
      return <Tag>{customerEventMapping[record.type]}</Tag>;
    },
  },
  {
    title: '备注',
    hideInForm: true,
    hideInSearch: true,
    width: '20%',
    render: (_, record) => {
      const { setCustomerEventCustomer } = useContext(recfreshContext);
      return (
        <>
          <Button
            type="link"
            onClick={() => {
              setCustomerEventCustomer?.({
                id: record.customerId,
                name: record.customerName,
              });
            }}
          >
            {record.remark && <span>[{record.remark}]</span>}
            <span>{record.comment}</span>
          </Button>
        </>
      );
    },
  },
  {
    title: '跟进时间',
    order: 1,
    render: (_, record) => {
      return (
        <span>{dayjs(record.createdAt).format('YYYY-MM-DD HH:mm:ss')}</span>
      );
    },
    search: {
      transform: (value) => {
        return {
          startTime: formatStartTime(dayjs(value[0])),
          endTime: formatEndTime(dayjs(value[1])),
        };
      },
    },
    valueType: 'dateRange',
    fieldProps: {
      // disabledDate: (current: dayjs.Dayjs) => {
      //   const startDate = current.startOf('day');
      //   const endDate = current.endOf('day');
      //   const now = dayjs().endOf('day');
      //   return startDate.isAfter(now);
      // },
      onChange: (dates: [dayjs.Dayjs, dayjs.Dayjs]) => {
        if (dates && dates[0] && dates[1]) {
          const diff = dates[1].diff(dates[0], 'day');
          if (diff > 7) {
            message.warning('时间跨度不能超过7天');
            return false;
          }
        }
        return true;
      },
    },
  },
];

export default () => {
  const { id: companyId } = useCompany();
  const [user, setUser] = useState<{ id: I.ID; name: string } | null>(null);
  const [customer, setCustomer] = useState<{
    id: I.ID;
    name: string;
    pool?: CustomerPoolEnum;
  }>();
  const [customerEventCustomer, setCustomerEventCustomer] = useState<{
    id: I.ID;
    name: string;
  }>();

  const ref = useRef<ActionType>();

  const location = useLocation();

  const query = useMemo(() => {
    return new URLSearchParams(location.hash.slice(1));
  }, [location.hash]);

  return (
    <recfreshContext.Provider
      value={{
        refresh: () => ref.current?.reload?.(),
        setUser,
        setCustomer,
        setCustomerEventCustomer,
      }}
    >
      {customer?.id && (
        <CustomerDetail
          id={customer.id}
          onCancel={() => setCustomer(undefined)}
          open={!!customer.id}
          readOnly
          withComment={false}
        />
      )}
      {user?.id && (
        <UserModal
          id={user.id}
          name={user.name}
          open={!!user}
          onCancel={() => setUser(null)}
        />
      )}
      {customerEventCustomer?.id && (
        <LastEventDrawer
          pool={customer?.pool || CustomerPoolEnum.PUBLIC}
          open={!!customerEventCustomer}
          onCancel={() => setCustomerEventCustomer(undefined)}
          customerId={customerEventCustomer.id}
          customerName={customerEventCustomer.name}
        />
      )}
      <ProTable<
        CustomerEventListItem,
        ListCustomerEventsParamsDTO & {
          query: URLSearchParams;
        }
      >
        className="pro-table"
        pagination={{
          pageSizeOptions: [20, 50, 100, 200, 500, 1000],
        }}
        actionRef={ref}
        columns={columns}
        options={false}
        params={{
          query: query,
        }}
        bordered
        rowKey="id"
        size="small"
        cardBordered={{ search: true }}
        dateFormatter="number"
        request={async (
          { current, pageSize, query, ...params },
          sort,
          filter,
        ) => {
          const eType = params.type || [CustomerEventTypeEnum.REMARK];
          return customerController
            .listCustomerEvents(companyId, current, pageSize, {
              ...params,
              pool: CustomerPoolEnum.BIZ,
              type: eType,
            })
            .then((data) => {
              return { data: data.list, success: true, total: data.total };
            })
            .catch((e) => {
              console.log(e.message);
              message.error(e.message);
              return { data: [], success: true, total: 0 };
            });
        }}
      />
    </recfreshContext.Provider>
  );
};
